import React from "react";
import { formatAmountWithCurrency } from "../../../../../../utils/CurrencyUtils";
import DateCell from "../DateCell";
import {
  StepHeader,
  Table,
  Empty,
  Notice,
  byDate,
  payoutLabel,
  StatusPill,
  rowStyle,
} from "../primitives";

// Where the date comes from. No longer its own column — it rides along as the
// tooltip on the padlock, which is the only place it matters: when you wonder
// why a cell will not let you type in it.
const SOURCE_LABEL = {
  standard: "Start / end of the scope",
  derived: "Last mapped activity",
  manual: "Entered by hand",
};

const tdStyle = { padding: "10px 16px" };
const tdDarkStyle = { padding: "10px 16px", color: "#0d0d0e" };
const tableShellStyle = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  overflow: "hidden",
};
const sectionStyle = { display: "flex", flexDirection: "column", gap: "12px" };

/**
 * Step 3 — billing milestone dates, and the contractor payouts that copy them.
 *
 * Only milestones with no mapped activities are typed by hand; the rest are
 * calculated. Payouts are shown read-only so the effect of a date change is
 * visible in the same place it is made.
 */
const BillingMilestonesStep = ({
  schedule,
  editable,
  preview,
  setGroupDrafts,
  groupValue,
}) => {
  // Derived dates follow the activity dates edited a step earlier, which are
  // not saved yet — so the stored due_date is stale here. The preview already
  // computed where each one lands; show that instead of the old value.
  const projectedGroup = new Map(
    (preview?.plan?.groups || []).map((g) => [g.group_id, g.to])
  );
  const projectedPayout = new Map(
    (preview?.plan?.payouts || []).map((p) => [p.id, p.to])
  );

  const shownGroupDate = (group) =>
    projectedGroup.has(group.group_id)
      ? projectedGroup.get(group.group_id)
      : groupValue(group);

  // Almost every cell in these two tables is locked, so sorting on the date
  // actually displayed costs nothing — there is no field to lose focus on.
  const groups = [...(schedule.billing_milestones || [])].sort(
    byDate(shownGroupDate)
  );
  const needsDate = groups.filter((g) => g.editable_date && !groupValue(g));

  // A payout is a copy of its billing milestone, so it should read like one.
  // Showing the raw reference_type made the two tables look unrelated —
  // "PAYMENT_GROUP" gives no hint that it is the Mobilisation payout.
  const groupById = new Map(groups.map((g) => [g.group_id, g]));

  const payouts = [...(schedule.contractor_payouts || [])]
    .map((p) => {
      const group = groupById.get(p.group_id);
      return {
        ...p,
        label: payoutLabel(p, group?.name),
        // A payout IS its billing milestone's date, so show the milestone's
        // date — not the payout's own stored one. Those two have drifted apart
        // in the data (the milestone was left null while the payout got a
        // date), and rendering the stale value made it look as though a
        // milestone with no date still had a payout scheduled.
        shownDate: group
          ? shownGroupDate(group)
          : projectedPayout.has(p.id)
            ? projectedPayout.get(p.id)
            : p.due_date,
      };
    })
    .sort(byDate((p) => p.shownDate));

  return (
    // Even rhythm: each section keeps its heading tight to its table, and the
    // two sections are separated by a single larger gap.
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <div style={sectionStyle}>
      <StepHeader
        title="Billing Milestones" />

      {needsDate.length > 0 && (
        <Notice tone="error">
          {needsDate.map((g) => g.name).join(", ")} —{" "}
          {needsDate.length === 1 ? "this milestone has" : "these milestones have"}{" "}
          no mapped activities and no due date. Enter one below.
        </Notice>
      )}

      <div style={tableShellStyle}>
        {!groups.length ? (
          <Empty>No billing milestones on this scope.</Empty>
        ) : (
          // Same column order as the payouts table below, so the two line up
          // when read one after the other.
          <Table columns={["Milestone", "Amount", "Due date", "Status"]}>
            {groups.map((group) => {
              const isManual = editable && group.editable_date;
              const stored = groupValue(group);
              const shown = isManual
                ? stored
                : projectedGroup.has(group.group_id)
                  ? projectedGroup.get(group.group_id)
                  : stored;

              return (
                <tr key={group.group_id} style={rowStyle}>
                  <td style={tdDarkStyle}>{group.name}</td>
                  <td style={tdDarkStyle}>
                    {group.amount ? formatAmountWithCurrency(group.amount, "black-xs-small") : "-"}
                  </td>
                  <td style={tdStyle}>
                    <DateCell
                      value={shown}
                      originalValue={isManual ? group.due_date : stored}
                      onChange={(date) =>
                        setGroupDrafts((prev) => ({
                          ...prev,
                          [group.group_id]: date,
                        }))
                      }
                      locked={!isManual}
                      lockReason={SOURCE_LABEL[group.date_source]}
                      placeholder="Select date"
                      hideLockIcon
                    />
                  </td>
                  <td style={tdStyle}>
                    <StatusPill status={group.status} />
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </div>
      </div>

      <div style={sectionStyle}>
      <StepHeader
        title="Contractor Payouts" />

      <div style={tableShellStyle}>
        {!payouts.length ? (
          <Empty>No contractor payouts on this scope yet.</Empty>
        ) : (
          <Table columns={["Payout", "Amount", "Due date", "Status"]}>
            {payouts.map((payout) => (
              <tr key={payout.id} style={rowStyle}>
                <td style={tdDarkStyle}>{payout.label}</td>
                <td style={tdDarkStyle}>
                  {payout.amount ? formatAmountWithCurrency(payout.amount, "black-xs-small") : "-"}
                </td>
                <td style={tdStyle}>
                  <DateCell
                    value={payout.shownDate}
                    originalValue={payout.due_date}
                    onChange={() => {}}
                    locked
                    placeholder="Select date"
                    hideLockIcon
                    lockReason={
                      payout.locked
                        ? "Payout already released"
                        : "Follows the billing milestone"
                    }
                  />
                </td>
                <td style={tdStyle}>
                  <StatusPill status={payout.status} />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      </div>
    </div>
  );
};

export default BillingMilestonesStep;
